import React, { useCallback, useEffect, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isBlank, notBlank } from "../../util/ext";
import Paper from "@mui/material/Paper";
import { useConfirm } from "../../common/use-confirm";
import {
  any,
  append,
  difference,
  find,
  includes,
  map,
  propEq,
  reject,
  union,
  without,
} from "ramda";
import Grid from "@mui/material/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import DialogContentText from "@mui/material/DialogContentText";
import { useLibraryOptionCreate, useLibraryOptions } from "../cspServices";
import List from "@mui/material/List";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { caseInsensitiveSort } from "../../util/sorts";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";

const sortLibraryOptions = caseInsensitiveSort("libraryName");

const LibraryOptionsDialog = ({ open, onCancel, onApply, project }) => {
  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);

  const librariesResponse = useLibraryOptions();
  useEffect(() => {
    if (librariesResponse.isSuccess) {
      if (notBlank(project.libraryOptions)) {
        setSelected(sortLibraryOptions(project.libraryOptions));
        const unused = reject(
          lib =>
            any(
              propEq("localLibraryOptionId", lib.localLibraryOptionId),
              project.libraryOptions,
            ),
          librariesResponse.data,
        );

        setAvailable(sortLibraryOptions(unused));
      } else {
        setAvailable(sortLibraryOptions(librariesResponse.data));
      }
    }
  }, [
    librariesResponse.isSuccess,
    librariesResponse.data,
    project.libraryOptions,
  ]);

  const [leftChecked, setLeftChecked] = useState([]);
  const [rightChecked, setRightChecked] = useState([]);
  const handleToggleOptionLeft = useCallback(
    lib => event => {
      if (event.target.checked) {
        setLeftChecked(prev => append(lib, prev));
      } else {
        setLeftChecked(prev => without([lib], prev));
      }
    },
    [],
  );

  const handleToggleOptionRight = useCallback(
    lib => event => {
      if (event.target.checked) {
        setRightChecked(prev => append(lib, prev));
      } else {
        setRightChecked(prev => without([lib], prev));
      }
    },
    [],
  );

  const handleCheckedRight = useCallback(() => {
    setSelected(prev => sortLibraryOptions(union(prev, leftChecked)));
    setAvailable(sortLibraryOptions(without(leftChecked, available)));
    setLeftChecked([]);
  }, [leftChecked, available]);

  const handleCheckedLeft = useCallback(() => {
    setAvailable(prev => sortLibraryOptions(union(prev, rightChecked)));
    setSelected(sortLibraryOptions(without(rightChecked, selected)));
  }, [rightChecked, selected]);

  const handleAllRight = useCallback(() => {
    setSelected(prev => sortLibraryOptions(union(prev, available)));
    setAvailable([]);
    setLeftChecked([]);
  }, [available]);

  const handleAllLeft = useCallback(() => {
    setAvailable(prev => sortLibraryOptions(union(prev, selected)));
    setSelected([]);
    setRightChecked([]);
  }, [selected]);

  const [libraryName, setLibraryName] = useState(null);
  const [error, setError] = useState(null);

  const libraryOptionCreate = useLibraryOptionCreate();
  const handleAddLibraryClick = useCallback(() => {
    const existing = find(
      propEq("libraryName", libraryName),
      union(available, selected),
    );
    if (notBlank(existing)) {
      setError("A Library with the specified name already exists.");
    } else {
      try {
        libraryOptionCreate.mutateAsync({ data: { libraryName } });
        setLibraryName(null);
        setError(null);
      } catch (e) {
        setError(e.message);
      }
    }
  }, [libraryName, available, selected, libraryOptionCreate]);

  const handleClose = useCallback(() => {
    const previous = notBlank(project.libraryOptions)
      ? project.libraryOptions
      : [];
    if (isBlank(difference(previous, selected))) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected libraries without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [selected, project.libraryOptions, showConfirm, onCancel]);

  const handleApply = useCallback(() => {
    onApply(selected);
  }, [onApply, selected]);

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle id="add-submission-types-dialog-title">
        Update Project Library Options
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Add a new Library to the list of Available Libraries.
        </DialogContentText>
        <Box mb={5}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item md={8}>
              <TextField
                id="library-name"
                fullWidth
                label="Library Name"
                InputProps={{
                  style: { fontSize: "1.8rem" },
                }}
                value={libraryName || ""}
                onChange={event => setLibraryName(event.target.value)}
                error={notBlank(error)}
                helperText={error}
              />
            </Grid>
            <Grid item md={3}>
              <Button
                variant="outlined"
                onClick={handleAddLibraryClick}
                disabled={isBlank(libraryName)}
                aria-label="add library"
              >
                Add Library
              </Button>
            </Grid>
          </Grid>
        </Box>

        <DialogContentText>
          Check the Libraries that should allowed for this Project.
        </DialogContentText>
        <Paper>
          <Box display="flex" alignItems="flex-start" mt={3}>
            <Box>
              <Typography variant="h5">Available Libraries</Typography>
              <Box mt={1} boxShadow={5}>
                <List
                  dense
                  style={{ height: 600, minWidth: 350, overflowY: "auto" }}
                >
                  {map(
                    libraryOption => (
                      <ListItem
                        key={libraryOption.localLibraryOptionId}
                        role="listitem"
                        button
                        onClick={handleToggleOptionLeft(libraryOption)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            color="primary"
                            checked={includes(libraryOption, leftChecked)}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{
                              "aria-labelledby": `available-item-${libraryOption.localLibraryOptionId}-label`,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={`available-item-${libraryOption.localLibraryOptionId}-label`}
                          primary={libraryOption.libraryName}
                        />
                      </ListItem>
                    ),
                    available,
                  )}
                </List>
              </Box>
            </Box>

            <Box height={600} display="flex" alignItems="center" ml={5} mr={5}>
              <Grid
                container
                spacing={2}
                direction="column"
                alignItems="center"
              >
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleAllRight}
                    disabled={available.length === 0}
                    aria-label="move all right"
                  >
                    <LastPageIcon fontSize="large" />
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleCheckedRight}
                    disabled={leftChecked.length === 0}
                    aria-label="move selected right"
                  >
                    <KeyboardArrowRightIcon fontSize="large" />
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleCheckedLeft}
                    disabled={rightChecked.length === 0}
                    aria-label="move selected left"
                  >
                    <KeyboardArrowLeftIcon fontSize="large" />
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleAllLeft}
                    disabled={selected.length === 0}
                    aria-label="move all left"
                  >
                    <FirstPageIcon fontSize="large" />
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="h5">Selected Libraries</Typography>
              <Box mt={1} boxShadow={5}>
                <List
                  dense
                  style={{ height: 600, minWidth: 350, overflowY: "auto" }}
                >
                  {map(
                    libraryOption => (
                      <ListItem
                        key={libraryOption.localLibraryOptionId}
                        role="listitem"
                        button
                        onClick={handleToggleOptionRight(libraryOption)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            color="primary"
                            checked={includes(libraryOption, rightChecked)}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{
                              "aria-labelledby": `selected-item-${libraryOption.localLibraryOptionId}-label`,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={`selected-item-${libraryOption.localLibraryOptionId}-label`}
                          primary={libraryOption.libraryName}
                        />
                      </ListItem>
                    ),
                    selected,
                  )}
                </List>
              </Box>
            </Box>
          </Box>
        </Paper>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LibraryOptionsDialog;
